import React, { useState, Children } from 'react';
import { useTranslation } from 'react-i18next';
import { Reservation } from '../../../shared/types/Reservation';
import { SearchIcon, FilterIcon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
export function Reservations() {
  const {
    t
  } = useTranslation('rooms');
  // Mock data based on the screenshot
  const [reservations, setReservations] = useState<Reservation[]>([{
    id: '1',
    pnr: '3Y6TE994',
    dateCreated: '27 March 2026 at 17:14',
    checkInDate: '09 Apr 2026',
    checkOutDate: '10 Apr 2026',
    mainGuest: 'Simon Verraest',
    guests: {
      adults: 2,
      children: 0,
      infants: 0
    },
    total: formatCurrencyAmount(1.29, {
      fractionDigits: 2
    }),
    status: 'confirmed'
  }, {
    id: '2',
    pnr: 'FRUJDGE',
    dateCreated: '26 March 2026 at 17:08',
    checkInDate: '10 May 2026',
    checkOutDate: '11 May 2026',
    mainGuest: 'Ahmed Waleed',
    guests: {
      adults: 2,
      children: 0,
      infants: 0
    },
    total: formatCurrencyAmount(1.29, {
      fractionDigits: 2
    }),
    status: 'confirmed'
  }, {
    id: '3',
    pnr: 'DERUVIM34',
    dateCreated: '25 March 2026 at 19:15',
    checkInDate: '28 Mar 2026',
    checkOutDate: '29 Mar 2026',
    mainGuest: 'Simon Verraest',
    guests: {
      adults: 2,
      children: 0,
      infants: 0
    },
    total: formatCurrencyAmount(1.29, {
      fractionDigits: 2
    }),
    status: 'confirmed'
  }]);
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // Filter reservations based on search term and status filter
  const filteredReservations = reservations.filter(reservation => {
    const matchesSearch = reservation.mainGuest.toLowerCase().includes(searchTerm.toLowerCase()) || reservation.pnr.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || reservation.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  // Format guest count for display
  function formatGuestCount(guests: {
    adults: number;
    children: number;
    infants: number;
  }): string {
    return `${guests.adults} Adults, ${guests.children} Children, ${guests.infants} Infants`;
  }
  // Navigate to reservation detail
  const navigateToDetail = (pnr: string) => {
    window.location.href = `/reservation-detail?pnr=${pnr}`;
  };
  return <div className="space-y-6" data-id="element-10335" data-mp="3rafi3tinx74n">
      <div className="flex justify-between items-center" data-id="element-10336" data-mp="3r9e5hkm00ckt">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-10337" data-mp="3mauh7djxmopa">
          {t('pages.Reservations.reservations')}
        </h1>
      </div>
      {/* Search and filters */}
      <div className="flex flex-wrap gap-3" data-id="element-10338" data-mp="1rx2yzpnjewi8">
        <div className="relative flex-grow max-w-xs" data-id="element-10339" data-mp="1ry4mwupnjdhd">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-10340" data-mp="7f0tvs9wihtb">
            <SearchIcon size={16} className="text-gray-400" data-id="element-10341" data-mp="ce403g1mau2v" />
          </div>
          <input type="search" aria-label={t('pages.Reservations.attrs.search_reservations')} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md" placeholder={t('pages.Reservations.attrs.search_reservations')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-10342" data-mp="cg82ftjvbl11" />
        </div>
        <select className="border border-gray-300 rounded-md px-3 py-2" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-10343" data-mp="3pubc6dgd5n12">
          <option value="all" data-id="element-10344" data-mp="3fvcf7s4ynftw">{t('components.AddRoomForm.status')}</option>
          <option value="confirmed" data-id="element-10345" data-mp="1a1m4keo8xa12">
            {t('pages.ReservationDetail.confirmed')}
          </option>
          <option value="cancelled" data-id="element-10346" data-mp="1e2fsh81ouoty">{t('pages.Reservations.cancelled')}</option>
          <option value="pending" data-id="element-10347" data-mp="pd35s0l59s0u">{t('pages.Reservations.pending')}</option>
        </select>
      </div>
      {/* Reservations Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto" data-id="element-10348" data-mp="ke9qc2marqy1">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-10349" data-mp="fdc9btd0x2y4">
          <thead className="bg-gray-50" data-id="element-10350" data-mp="afstwq4lak4l">
            <tr data-id="element-10351" data-mp="af10nlygsyhh">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10352" data-mp="ahn71i9md7y4">
                {t('pages.Reservations.date_created')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10353" data-mp="3st0jgjiili3x">
                {t('pages.Reservations.pnr')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10354" data-mp="3iu2nwwmo8obf">
                {t('pages.Reservations.checkin_date')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10355" data-mp="1ydbjgq76p19e">
                {t('pages.Reservations.checkout_date')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10356" data-mp="28ag4unt7gsp5">
                {t('pages.Reservations.main_guest')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10357" data-mp="1fkhdumvgalhu">
                {t('pages.Reservations.guests')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10358" data-mp="rxrb1jba2mw6">
                {t('pages.ReservationDetail.total')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-10359" data-mp="ku1dfllurwdh">
            {filteredReservations.map(reservation => <tr key={reservation.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigateToDetail(reservation.pnr)} data-id="element-10360" data-mp="ylzhvj4md43b">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-10361" data-mp="5w8yovo4rc0c">
                  {reservation.dateCreated}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-10362" data-mp="1x04jpp6hc49">
                  {reservation.pnr}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-10363" data-mp="avc26bogpa67">
                  {reservation.checkInDate}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-10364" data-mp="1r8agwpckuwy0">
                  {reservation.checkOutDate}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-10365" data-mp="3485ghvmt5qqk">
                  {reservation.mainGuest}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-10366" data-mp="3991oakaa5x19">
                  {formatGuestCount(reservation.guests)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-10367" data-mp="306aa4cq0sn5x">
                  {reservation.total}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}